/**
 * Card processing-fee exemption for migrated mutual policies (#36230; Joe 2026-10-14).
 *
 * CFM members migrated from Guidewire InsuranceNow paid no card processing fee in GO, so the member
 * is not surcharged on the imported term, nor on any renewal in the imported lineage whose rate control
 * date is before a brand-configured cutoff (CFM: 2027-06-01). CFM absorbs the processing fee instead:
 * the charge keeps the normal platform route, where the connected account's pricing scheme takes it
 * from CFM's share. Exempt means no card-fee line in the schedule, no surcharge on a card payment link,
 * and no card-fee disclosure.
 *
 * Shared here (audience: universal) so billing, the direct-bill payment links, the invoice renderer
 * and staff-fe all make the identical decision.
 */
import { isOriginalImportedTerm } from './migrated-renewal-billing';

/** Brand-configured exemption (backend `fees.farm.cardFeeExemption`, frontend top-level `cardFeeExemption`). */
export interface CardFeeExemptionConfig {
  /** The term a converter imported is exempt. */
  importedTerm?: boolean;
  /** Every renewal in an imported lineage whose rate control date is strictly before this ISO date is exempt. */
  importedLineageRateControlDateBefore?: string | null;
}

export interface CardFeeExemptionTermFields {
  source?: string | null;
  wasRenewedFromImportedTerm?: boolean | null;
  /** The term's rate control date (renewals carry it top-level; the policy global carries it too). */
  rateControlDate?: string | null;
  global?: { rateControlDate?: string | null } | null;
}

/** Whether no card fee may be charged to the member on this term. False for any brand with no exemption. */
export function isCardFeeExemptTerm(
  policy: CardFeeExemptionTermFields | null | undefined,
  exemption: CardFeeExemptionConfig | null | undefined
): boolean {
  if (!policy || !exemption) {
    return false;
  }
  if (isOriginalImportedTerm(policy)) {
    return Boolean(exemption.importedTerm);
  }
  const cutoff = exemption.importedLineageRateControlDateBefore;
  const rateControlDate = policy.rateControlDate ?? policy.global?.rateControlDate;
  return (
    policy.wasRenewedFromImportedTerm === true && !!cutoff && !!rateControlDate && rateControlDate.slice(0, 10) < cutoff
  );
}
